// Server functions for EnvironmentSetting operations
import { createServerFn } from '@tanstack/react-start'
import { getRequest } from '@tanstack/react-start/server'
import { db } from '@/src/server/db'
import { auth } from '@/auth'
import { getSessionFromRequest } from '@/src/server/lib/accessControl'
import { JsonHelpers } from '@/src/server/lib/prismaJsonHelpers'
import { normalizeCronSettings, type CronSettings } from '@/src/lib/cronJobs'
import { z } from 'zod'

// Zod schemas for input validation
const GetEnvironmentSettingInput = z.object({
  key: z.string(),
})

const UpsertEnvironmentSettingInput = z.object({
  key: z.string(),
  value: z.any(),
  description: z.string().optional(),
})

const UpsertCronSettingsInput = z.object({
  value: z.any(),
})

const CRON_SETTINGS_KEY = 'cron_jobs'

export const getEnvironmentSetting = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown) => GetEnvironmentSettingInput.parse(data))
  .handler(async ({ data }) => {
    const setting = await db.environmentSetting.findUnique({ where: { key: data.key } })
    if (!setting) return null

    // Validate and normalize JSON value using JsonHelpers
    const parsed = JsonHelpers.environmentValue.fromDb(setting.value)
    const normalizedValue = parsed && typeof parsed === 'object' && parsed !== null ? parsed : {}

    return {
      ...setting,
      value: normalizedValue,
    }
  })

export const upsertEnvironmentSetting = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpsertEnvironmentSettingInput.parse(data))
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    if (!session?.user) throw new Error('Unauthorized')

    // Check if user has admin permissions
    const user = await db.user.findUnique({
      where: { id: session.user.id },
      include: { roleDetails: true }
    })
    if (!user?.roleDetails?.permissions.includes('*')) {
      throw new Error('Unauthorized: Admin access required')
    }

    const setting = await db.environmentSetting.upsert({
      where: { key: data.key },
      create: {
        key: data.key,
        value: data.value,
        description: data.description,
      },
      update: {
        value: data.value,
        description: data.description,
      },
    })

    const parsed = JsonHelpers.environmentValue.fromDb(setting.value)
    const normalizedValue = parsed && typeof parsed === 'object' && parsed !== null ? parsed : {}

    return {
      ...setting,
      value: normalizedValue,
    }
  })

export const listEnvironmentSettings = createServerFn({ method: 'GET' })
  .handler(async () => {
    const session = await getSessionFromRequest()
    if (!session?.user) throw new Error('Unauthorized')

    // Check if user has admin permissions
    const user = await db.user.findUnique({
      where: { id: session.user.id },
      include: { roleDetails: true }
    })
    if (!user?.roleDetails?.permissions.includes('*')) {
      throw new Error('Unauthorized: Admin access required')
    }

    const settings = await db.environmentSetting.findMany()
    // Validate and normalize `value` for all entries
    return settings.map(s => {
      const parsed = JsonHelpers.environmentValue.fromDb(s.value)
      return {
        ...s,
        value: parsed && typeof parsed === 'object' && parsed !== null ? parsed : {},
      }
    })
  })

export const getCronSettings = createServerFn({ method: 'GET' })
  .handler(async (): Promise<CronSettings> => {
    const session = await getSessionFromRequest()
    if (!session?.user) throw new Error('Unauthorized')

    const user = await db.user.findUnique({
      where: { id: session.user.id },
      include: { roleDetails: true },
    })
    if (!user?.roleDetails?.permissions.includes('*')) {
      throw new Error('Unauthorized: Admin access required')
    }

    const setting = await db.environmentSetting.findUnique({
      where: { key: CRON_SETTINGS_KEY },
    })

    if (!setting) {
      return normalizeCronSettings(null)
    }

    return normalizeCronSettings(setting.value)
  })

export const upsertCronSettings = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => UpsertCronSettingsInput.parse(data))
  .handler(async ({ data }): Promise<CronSettings> => {
    const session = await getSessionFromRequest()
    if (!session?.user) throw new Error('Unauthorized')

    const user = await db.user.findUnique({
      where: { id: session.user.id },
      include: { roleDetails: true },
    })
    if (!user?.roleDetails?.permissions.includes('*')) {
      throw new Error('Unauthorized: Admin access required')
    }

    const normalized = normalizeCronSettings(data.value)

    await db.environmentSetting.upsert({
      where: { key: CRON_SETTINGS_KEY },
      create: {
        key: CRON_SETTINGS_KEY,
        value: normalized,
        description: 'Central cron scheduler settings used by cron-processor',
      },
      update: {
        value: normalized,
      },
    })

    return normalized
  })

// ─── Workflow URL Allowlist (global default) ─────────────────────────────────

const WORKFLOW_URL_ALLOWLIST_KEY = 'workflow_url_allowlist'

/** Read the global workflow URL allowlist. Returns null if not set (= unrestricted). */
export const getWorkflowUrlAllowlist = createServerFn({ method: 'GET' })
  .handler(async () => {
    const session = await getSessionFromRequest()
    if (!session?.user) throw new Error('Unauthorized')

    const setting = await db.environmentSetting.findUnique({
      where: { key: WORKFLOW_URL_ALLOWLIST_KEY },
    })
    if (!setting) return null
    const value = setting.value
    if (Array.isArray(value)) return value as string[]
    return null
  })

/** Update the global workflow URL allowlist (admin only). */
export const upsertWorkflowUrlAllowlist = createServerFn({ method: 'POST' })
  .inputValidator((data: unknown) => {
    const schema = z.object({ entries: z.array(z.string()) })
    return schema.parse(data)
  })
  .handler(async ({ data }) => {
    const session = await getSessionFromRequest()
    if (!session?.user) throw new Error('Unauthorized')

    const user = await db.user.findUnique({
      where: { id: session.user.id },
      include: { roleDetails: true },
    })
    if (!user?.roleDetails?.permissions.includes('*')) {
      throw new Error('Unauthorized: Admin access required')
    }

    await db.environmentSetting.upsert({
      where: { key: WORKFLOW_URL_ALLOWLIST_KEY },
      create: {
        key: WORKFLOW_URL_ALLOWLIST_KEY,
        value: data.entries,
        description: 'Global URL allowlist for workflow API_CALL and MCP_CALL steps. Leave empty to allow all external URLs.',
      },
      update: { value: data.entries },
    })

    return { entries: data.entries }
  })
// This marker ensures the append is safe

